import { useMemo, useState } from "react";
import { ChevronDown, ChevronRight, Lock, RefreshCw, Search } from "lucide-react";
import { Input } from "@/components/atoms/studio/ui/input";
import { Skeleton } from "@/components/atoms/studio/ui/skeleton";
import { cn } from "@/lib/utils";
import type { McpConnector, McpGatewayTool } from "@/services/mcp-gateway";
import { allowedFirst, blockedToolIds, groupToolsByConnector, humanToolName } from "./toolsPageModel";

const COLLAPSED_ROWS = 8;
// Literal class strings so Tailwind generates them; rows stay one column below lg.
const ROW_COLS = "lg:grid-cols-[minmax(200px,1fr)_minmax(260px,1.6fr)_minmax(160px,0.8fr)]";
const ROW_COLS_READ_ONLY = "lg:grid-cols-[minmax(200px,1fr)_minmax(260px,1.6fr)_minmax(160px,0.8fr)_100px]";

function shortToolDescription(description: string): string {
  const text = description.replace(/<example[\s\S]*?<\/example>/gi, " ").replace(/```[\s\S]*?```/g, " ").replace(/[#*_`>{}\[\]\t]/g, " ").replace(/\s+/g, " ").trim();
  if (!text) return "No description returned by the MCP endpoint.";
  const sentence = text.match(/^(.{30,220}?[.!?])\s/)?.[1] ?? text.slice(0, 180);
  return sentence.length < text.length ? `${sentence.replace(/[.,;:\s]+$/, "")}.` : sentence;
}

function schemaInputNames(schema: Record<string, unknown>): { required: string[]; optional: string[] } {
  const required = Array.isArray(schema.required) ? schema.required.map((item) => String(item)).filter(Boolean) : [];
  const properties = schema.properties && typeof schema.properties === "object" && !Array.isArray(schema.properties) ? Object.keys(schema.properties as Record<string, unknown>) : [];
  return { required, optional: properties.filter((item) => !required.includes(item)) };
}

type ToolGroupsProps = {
  tools: McpGatewayTool[];
  connectors: McpConnector[];
  allowedByConnector: Map<string, Set<string>>;
  isFetching: boolean;
  isFetched: boolean;
  isError: boolean;
  onRefresh: () => void;
};

export function ToolGroups({ tools, connectors, allowedByConnector, isFetching, isFetched, isError, onRefresh }: ToolGroupsProps) {
  const [query, setQuery] = useState("");
  const [filter, setFilter] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<Set<string> | null>(null);
  const [showAll, setShowAll] = useState<Set<string>>(new Set());

  const blocked = useMemo(() => blockedToolIds(tools, allowedByConnector), [tools, allowedByConnector]);
  const groups = useMemo(
    () => groupToolsByConnector(tools, connectors).map((g) => ({ ...g, tools: allowedFirst(g.tools, blocked) })),
    [tools, connectors, blocked],
  );

  const q = query.trim().toLowerCase();
  const visibleGroups = groups
    .filter((g) => !filter || g.connectorId === filter)
    .map((g) => ({
      ...g,
      tools: q
        ? g.tools.filter((t) => t.name.toLowerCase().includes(q) || (t.description || "").toLowerCase().includes(q) || humanToolName(t.name, g.title).toLowerCase().includes(q))
        : g.tools,
    }))
    .filter((g) => g.tools.length);

  // First group open by default; a search or a filter opens every group it shows.
  const isOpen = (id: string) => !!q || !!filter || (expanded ? expanded.has(id) : id === groups[0]?.connectorId);
  const toggle = (id: string) => {
    const next = new Set(expanded ?? (groups[0] ? [groups[0].connectorId] : []));
    if (next.has(id)) next.delete(id); else next.add(id);
    setExpanded(next);
  };

  return (
    <div className="studio-card p-4">
      <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
        <div className="flex items-baseline gap-3">
          <h2 className="text-xl font-semibold">Tools</h2>
          <span className="text-sm text-muted-foreground">{tools.length} from {groups.length} service{groups.length === 1 ? "" : "s"}</span>
        </div>
        <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-center">
          {groups.length > 1 ? (
            <div className="flex flex-wrap gap-1 rounded-lg border border-border p-1 text-sm" role="group" aria-label="Filter tools by service">
              <button type="button" aria-pressed={!filter} onClick={() => setFilter(null)}
                className={cn("rounded-md px-3 py-1.5", !filter ? "bg-muted font-medium" : "text-muted-foreground hover:text-foreground")}>All</button>
              {groups.map((g) => (
                <button key={g.connectorId} type="button" aria-pressed={filter === g.connectorId} onClick={() => setFilter(g.connectorId)}
                  className={cn("rounded-md px-3 py-1.5", filter === g.connectorId ? "bg-muted font-medium" : "text-muted-foreground hover:text-foreground")}>{g.title}</button>
              ))}
            </div>
          ) : null}
          <button type="button" className="studio-button" onClick={onRefresh} disabled={isFetching}>
            <RefreshCw className={cn("h-4 w-4", isFetching && "animate-spin")} /> Refresh
          </button>
          <label className="flex h-10 min-w-[240px] items-center gap-2 rounded-md border border-border bg-card px-3">
            <Search className="h-4 w-4 text-muted-foreground" />
            <span className="sr-only">Search tools</span>
            <Input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search tools" className="h-auto border-0 bg-transparent px-0 shadow-none focus-visible:ring-0" />
          </label>
        </div>
      </div>

      {isFetching && !tools.length ? (
        <div className="mt-5 grid gap-3">{Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} className="h-14 rounded-md" />)}</div>
      ) : isError ? (
        <div className="mt-5 rounded-md border border-destructive/30 bg-destructive/5 p-4 text-sm text-destructive">Could not load MCP tools.</div>
      ) : !isFetched || !groups.length ? (
        <div className="mt-5 rounded-md border bg-muted/30 p-4 text-sm text-muted-foreground">Connect a service above to see its tools here.</div>
      ) : !visibleGroups.length ? (
        <div className="mt-5 rounded-md border bg-muted/30 p-4 text-sm text-muted-foreground">No tools match “{query}”.</div>
      ) : (
        <div className="mt-5 space-y-3">
          {visibleGroups.map((g) => {
            const open = isOpen(g.connectorId);
            const readOnly = !!g.connector?.read_only;
            const totalBlocked = groups.find((x) => x.connectorId === g.connectorId)?.tools.filter((t) => blocked.has(t.id)).length ?? 0;
            const rows = open && !showAll.has(g.connectorId) && !q ? g.tools.slice(0, COLLAPSED_ROWS) : g.tools;
            return (
              <div key={g.connectorId} className="overflow-hidden rounded-md border border-border">
                <button type="button" aria-expanded={open} onClick={() => toggle(g.connectorId)}
                  className="flex w-full flex-wrap items-center gap-3 px-4 py-3 text-left hover:bg-muted/40">
                  {open ? <ChevronDown className="h-4 w-4 text-muted-foreground" /> : <ChevronRight className="h-4 w-4 text-muted-foreground" />}
                  <span className="font-semibold">{g.title}</span>
                  <span className="text-sm text-muted-foreground">{g.tools.length} tool{g.tools.length === 1 ? "" : "s"}</span>
                  {readOnly ? (
                    <span className="rounded-full bg-warning/10 px-2.5 py-0.5 text-xs font-semibold text-warning">
                      Read-only{totalBlocked ? ` · ${totalBlocked} blocked` : ""}
                    </span>
                  ) : null}
                </button>
                {open ? (
                  <div className="border-t border-border">
                    <div className={cn("hidden gap-4 bg-muted/40 px-4 py-2 text-xs font-semibold uppercase text-muted-foreground lg:grid", readOnly ? ROW_COLS_READ_ONLY : ROW_COLS)}>
                      <div>Tool</div><div>What it does</div><div>Needs</div>{readOnly ? <div>Access</div> : null}
                    </div>
                    {rows.map((t) => <ToolRow key={t.id} tool={t} groupTitle={g.title} readOnly={readOnly} blocked={blocked.has(t.id)} />)}
                    {open && !q && g.tools.length > COLLAPSED_ROWS && !showAll.has(g.connectorId) ? (
                      <div className="px-4 py-3">
                        <button type="button" className="text-sm font-medium text-primary" onClick={() => setShowAll(new Set(showAll).add(g.connectorId))}>
                          Show all {g.tools.length} {g.title} tools
                        </button>
                      </div>
                    ) : null}
                  </div>
                ) : null}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function ToolRow({ tool, groupTitle, readOnly, blocked }: { tool: McpGatewayTool; groupTitle: string; readOnly: boolean; blocked: boolean }) {
  const { required, optional } = schemaInputNames(tool.schema || {});
  const visibleRequired = required.slice(0, 3);
  const visibleOptional = optional.slice(0, Math.max(0, 4 - visibleRequired.length));
  const hiddenInputCount = Math.max(0, required.length + optional.length - visibleRequired.length - visibleOptional.length);
  const summary = shortToolDescription(tool.description || "");

  return (
    <div className={cn("grid gap-3 border-t border-border/60 px-4 py-3 first:border-t-0 lg:items-start lg:gap-4", readOnly ? ROW_COLS_READ_ONLY : ROW_COLS, blocked && "text-muted-foreground")}>
        <div className="min-w-0">
          <div className={cn("text-sm font-medium", !blocked && "text-foreground")}>{humanToolName(tool.name, groupTitle)}</div>
          <div className="mt-0.5 break-words font-mono text-xs text-muted-foreground" title={tool.name}>{tool.name}</div>
        </div>
        <div className="min-w-0">
          <p className="text-sm leading-6 text-muted-foreground">{summary}</p>
          {tool.description && tool.description.length > summary.length + 40 ? (
            <details className="mt-1">
              <summary className="cursor-pointer text-xs font-semibold text-primary">Full description</summary>
              <pre className="mt-2 max-h-56 overflow-auto whitespace-pre-wrap rounded-md bg-muted/40 p-3 text-xs text-muted-foreground">{tool.description}</pre>
            </details>
          ) : null}
        </div>
        <div className="flex min-w-0 flex-wrap content-start gap-1.5">
          {visibleRequired.map((name) => <span key={`r-${name}`} className="rounded border border-primary/30 bg-primary/5 px-2 py-0.5 font-mono text-[11px] text-primary">{name}</span>)}
          {visibleOptional.map((name) => <span key={`o-${name}`} className="rounded border border-border bg-muted/30 px-2 py-0.5 font-mono text-[11px] text-muted-foreground">{name}</span>)}
          {hiddenInputCount ? <span className="rounded border border-border bg-muted/30 px-2 py-0.5 text-[11px] text-muted-foreground">+{hiddenInputCount}</span> : null}
          {!required.length && !optional.length ? <span className="text-sm text-muted-foreground">Nothing</span> : null}
        </div>
        {readOnly ? (
          <div className="text-xs font-semibold">
            {blocked
              ? <span className="inline-flex items-center gap-1 text-warning"><Lock className="h-3 w-3" /> Blocked</span>
              : <span className="text-success">Allowed</span>}
          </div>
        ) : null}
    </div>
  );
}
